import React from 'react';
import { Button, Empty, Popover, Spin } from 'antd';
import ProForm from '@ant-design/pro-form';
import { EmojiPicker } from 'frimousse';
import { CloseCircleOutlined } from '@ant-design/icons';
import styles from './EmojiSelectorFormItem.less';
import { getLocale, useIntl } from 'umi';
import { F } from '@/utils/intl';

interface EmojiSelectorProps {
  value?: string;
  onChange?: (val?: string) => void;
  disabled?: boolean;
}

const EmojiSelector: React.FC<EmojiSelectorProps> = ({ value, onChange, disabled }) => {
  const isEmpty = !value;
  const display = isEmpty ? '—' : value;
  const locale = getLocale()
  const intl = useIntl()
  if (disabled) {
    return <div className={`${styles.emojiSelectorTrigger} ${styles.emojiSelectorTriggerDisabled} ${isEmpty ? styles.emojiSelectorEmpty : ''}`}>{display}</div>;
  }

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      overlayClassName={styles.emojiPopoverOverlay}
      content={
        <div className={styles.emojiPickerContainer}>
          {!isEmpty && (
            <div className={styles.emojiPopoverActions}>
              <Button size="small" type="link" danger icon={<CloseCircleOutlined />} onClick={() => onChange?.(undefined)}>
                <F id={'common.actions.clear'} defaultMessage={'clear'}/>
              </Button>
            </div>
          )}
          <EmojiPicker.Root
            locale={locale?.split('-')?.[0] || 'en'}
            onEmojiSelect={(emoji) => {
              onChange?.(emoji.emoji);
            }}
          >
            <EmojiPicker.Search placeholder={intl.formatMessage({
              id: 'pages.lead.visibilitySettings.search',
              defaultMessage: 'Search...'
            })} />
            <EmojiPicker.Viewport>
              <EmojiPicker.Loading>
                <Spin />
              </EmojiPicker.Loading>
              <EmojiPicker.Empty>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </EmojiPicker.Empty>
              <EmojiPicker.List />
            </EmojiPicker.Viewport>
          </EmojiPicker.Root>
        </div>
      }
    >
      <div
        className={`${styles.emojiSelectorTrigger} ${isEmpty ? styles.emojiSelectorEmpty : ''}`}
        role="button"
        tabIndex={0}
        title={isEmpty ? 'No icon selected' : 'Selected icon'}
        aria-label={isEmpty ? 'No icon selected' : 'Selected icon'}
      >
        {display}
      </div>
    </Popover>
  );
};

interface EmojiSelectorFormItemProps {
  name: string;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  rules?: any[];
  disabled?: boolean;
}

const EmojiSelectorFormItem: React.FC<EmojiSelectorFormItemProps> = ({ name, label, tooltip, rules, disabled, ...props }) => {
  return (
    <ProForm.Item name={name} label={label} tooltip={tooltip} rules={rules} valuePropName="value" {...props}>
      <EmojiSelector disabled={disabled} />
    </ProForm.Item>
  );
};

export default EmojiSelectorFormItem;
